import React from "react";
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { AuthProvider } from "./data/providers/AuthProvider";
import { AppThemeProvider } from "./data/providers/AppThemeProvider";
import { SoulProvider } from "./data/providers/SoulProvider";
import AnalyticsPage from "./pages/AnalyticsPage";
import AdminPage from "./pages/AdminPage";
import BillingPage from "./pages/BillingPage";
import ChatsPage from "./pages/ChatsPage";
import Dashboard from "./pages/Dashboard";
import InvoicesPage, { InvoiceView, InvoiceViewGroup } from "./pages/InvoicesPage";
import LegalPage from "./pages/LegalPage";
import LoginPage from "./pages/LoginPage";
import NoAccessPage from "./pages/NoAccessPage";
import SettingsPage from "./pages/SettingsPage";
import TravelPage from "./pages/TravelPage";
import CarRentalPage from "./pages/CarRentalPage";
import StaysPage from "./pages/StaysPage";
import TestChatPage from "./pages/TestChatPage";
import PermissionRoute from "./routes/PermissionRoute";
import PrivateRoute from "./routes/PrivateRoute";

const invoiceViewGroups: Record<string, InvoiceViewGroup> = {
  sales: ["sales", "analytics"],
  catalog: ["items", "categories"],
  quotes: ["quotes"],
  people: ["customers", "sellers"],
  reminders: ["reminders"],
};

const invoiceViewGroupByView: Record<InvoiceView, InvoiceViewGroup> = {
  analytics: invoiceViewGroups.sales,
  "new-sale": invoiceViewGroups.sales,
  sales: invoiceViewGroups.sales,
  categories: invoiceViewGroups.catalog,
  items: invoiceViewGroups.catalog,
  quotes: invoiceViewGroups.quotes,
  "quote-history": ["quote-history"],
  customers: invoiceViewGroups.people,
  sellers: invoiceViewGroups.people,
  reminders: invoiceViewGroups.reminders,
};

const App: React.FC = () => {
  return (
    <AppThemeProvider>
      <AuthProvider>
        <SoulProvider>
          <Router>
            <Routes>
              <Route path="/" element={<LoginPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
              <Route path="/privacy-policy" element={<LegalPage kind="privacy" />} />
              <Route path="/terms" element={<LegalPage kind="terms" />} />
              <Route
                path="/data-deletion"
                element={<LegalPage kind="data-deletion" />}
              />
              <Route element={<PrivateRoute />}>
                <Route path="/dashboard" element={<Dashboard />}>
                  <Route
                    index
                    element={
                      <PermissionRoute path="/dashboard">
                        <AnalyticsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="conversations"
                    element={
                      <PermissionRoute path="/dashboard/conversations">
                        <AnalyticsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="chats"
                    element={
                      <PermissionRoute path="/dashboard/chats">
                        <ChatsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="settings"
                    element={
                      <PermissionRoute path="/dashboard/settings">
                        <SettingsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="meta-cloud-api"
                    element={
                      <PermissionRoute path="/dashboard/settings">
                        <SettingsPage cloudApiOnly />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="travel"
                    element={
                      <PermissionRoute path="/dashboard/travel">
                        <Navigate to="/dashboard/travel/settings" replace />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="travel/settings"
                    element={
                      <PermissionRoute path="/dashboard/travel/settings">
                        <TravelPage initialTab="settings" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="travel/design"
                    element={
                      <PermissionRoute path="/dashboard/travel/design">
                        <TravelPage initialTab="design" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="travel/pages"
                    element={
                      <PermissionRoute path="/dashboard/travel/pages">
                        <TravelPage initialTab="pages" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="travel/seo"
                    element={
                      <PermissionRoute path="/dashboard/travel/seo">
                        <TravelPage initialTab="seo" />
                      </PermissionRoute>
                    }
                  />
                  <Route path="travel/locations" element={<PermissionRoute path="/dashboard/travel/locations"><TravelPage initialTab="locations" /></PermissionRoute>} />
                  <Route path="travel/services" element={<PermissionRoute path="/dashboard/travel/services"><TravelPage initialTab="services" /></PermissionRoute>} />
                  <Route
                    path="travel/packages"
                    element={
                      <PermissionRoute path="/dashboard/travel/packages">
                        <TravelPage initialTab="packages" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="travel/domains"
                    element={
                      <PermissionRoute path="/dashboard/travel/domains">
                        <TravelPage initialTab="domains" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="travel/bookings"
                    element={
                      <PermissionRoute path="/dashboard/travel/bookings">
                        <TravelPage initialTab="bookings" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="car-rental"
                    element={
                      <PermissionRoute path="/dashboard/car-rental">
                        <Navigate to="/dashboard/car-rental/settings" replace />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="car-rental/settings"
                    element={
                      <PermissionRoute path="/dashboard/car-rental/settings">
                        <CarRentalPage initialTab="settings" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="car-rental/design"
                    element={
                      <PermissionRoute path="/dashboard/car-rental/design">
                        <CarRentalPage initialTab="design" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="car-rental/pages"
                    element={
                      <PermissionRoute path="/dashboard/car-rental/pages">
                        <CarRentalPage initialTab="pages" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="car-rental/seo"
                    element={
                      <PermissionRoute path="/dashboard/car-rental/seo">
                        <CarRentalPage initialTab="seo" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="car-rental/fleet"
                    element={
                      <PermissionRoute path="/dashboard/car-rental/fleet">
                        <CarRentalPage initialTab="fleet" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="car-rental/domains"
                    element={
                      <PermissionRoute path="/dashboard/car-rental/domains">
                        <CarRentalPage initialTab="domains" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="car-rental/reservations"
                    element={
                      <PermissionRoute path="/dashboard/car-rental/reservations">
                        <CarRentalPage initialTab="reservations" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="stays"
                    element={
                      <PermissionRoute path="/dashboard/stays">
                        <Navigate to="/dashboard/stays/settings" replace />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="stays/settings"
                    element={
                      <PermissionRoute path="/dashboard/stays/settings">
                        <StaysPage initialTab="settings" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="stays/design"
                    element={
                      <PermissionRoute path="/dashboard/stays/design">
                        <StaysPage initialTab="design" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="stays/pages"
                    element={
                      <PermissionRoute path="/dashboard/stays/pages">
                        <StaysPage initialTab="pages" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="stays/seo"
                    element={
                      <PermissionRoute path="/dashboard/stays/seo">
                        <StaysPage initialTab="seo" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="stays/properties"
                    element={
                      <PermissionRoute path="/dashboard/stays/properties">
                        <StaysPage initialTab="properties" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="stays/domains"
                    element={
                      <PermissionRoute path="/dashboard/stays/domains">
                        <StaysPage initialTab="domains" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="stays/reservations"
                    element={
                      <PermissionRoute path="/dashboard/stays/reservations">
                        <StaysPage initialTab="reservations" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="test-chat"
                    element={
                      <PermissionRoute path="/dashboard/test-chat">
                        <TestChatPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="admin"
                    element={
                      <PermissionRoute path="/dashboard/admin">
                        <AdminPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="billing"
                    element={
                      <PermissionRoute path="/dashboard/billing">
                        <BillingPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="invoices/items"
                    element={
                      <PermissionRoute path="/dashboard/invoices/items">
                        <InvoicesPage
                          view="items"
                          tabs={invoiceViewGroupByView.items}
                        />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="invoices/categories"
                    element={
                      <PermissionRoute path="/dashboard/invoices/categories">
                        <InvoicesPage
                          view="categories"
                          tabs={invoiceViewGroupByView.categories}
                        />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="invoices/customers"
                    element={
                      <PermissionRoute path="/dashboard/invoices/customers">
                        <InvoicesPage
                          view="customers"
                          tabs={invoiceViewGroupByView.customers}
                        />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="invoices/sellers"
                    element={
                      <PermissionRoute path="/dashboard/invoices/sellers">
                        <InvoicesPage
                          view="sellers"
                          tabs={invoiceViewGroupByView.sellers}
                        />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="invoices/reminders"
                    element={
                      <PermissionRoute path="/dashboard/invoices/reminders">
                        <InvoicesPage
                          view="reminders"
                          tabs={invoiceViewGroupByView.reminders}
                        />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="invoices/quotes"
                    element={
                      <PermissionRoute path="/dashboard/invoices/quotes">
                        <InvoicesPage
                          view="quotes"
                          tabs={invoiceViewGroupByView.quotes}
                        />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="invoices/quote-history"
                    element={
                      <PermissionRoute path="/dashboard/invoices/quote-history">
                        <InvoicesPage
                          view="quote-history"
                          tabs={invoiceViewGroupByView["quote-history"]}
                        />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="invoices/new-sale"
                    element={
                      <PermissionRoute path="/dashboard/invoices/new-sale">
                        <InvoicesPage
                          view="sales"
                          tabs={invoiceViewGroupByView.sales}
                        />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="invoices/sales"
                    element={
                      <PermissionRoute path="/dashboard/invoices/sales">
                        <InvoicesPage
                          view="sales"
                          tabs={invoiceViewGroupByView.sales}
                        />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="invoices/analytics"
                    element={
                      <PermissionRoute path="/dashboard/invoices/analytics">
                        <InvoicesPage
                          view="analytics"
                          tabs={invoiceViewGroupByView.analytics}
                        />
                      </PermissionRoute>
                    }
                  />
                  <Route path="no-access" element={<NoAccessPage />} />
                </Route>
              </Route>
            </Routes>
          </Router>
        </SoulProvider>
      </AuthProvider>
    </AppThemeProvider>
  );
};

export default App;
